import { useState, useEffect, useMemo } from "react";
import firebase from "../../../common/firebase";
import moment from "moment";
import {
  useDeviceLimitExceptionList,
  useAllSubscriptions,
  useAllInvites,
  useNYCampaignUsers,
  useCollectionCounts,
  useFreeTrials,
  useAllUsers,
  useStaffStudents,
} from "../utils/summaryDataQueries";

/**
 * Custom hook for fetching and processing all summary data
 * @returns {Object} Summary data, loading state, error state, and auth info
 */
export const useSummaryData = () => {
  const [uid, setUid] = useState(null);
  const [role, setRole] = useState(null);
  const [error, setError] = useState("");

  // Fetch device limit exception list first (needed for other queries)
  const { data: deviceLimitExceptionListData = [] } =
    useDeviceLimitExceptionList();

  // Memoize the deviceLimitExceptionList to prevent reference changes
  const deviceLimitExceptionList = useMemo(() => {
    return Array.isArray(deviceLimitExceptionListData)
      ? deviceLimitExceptionListData
      : [];
  }, [deviceLimitExceptionListData]);

  // Fetch all data using React Query hooks
  const subscriptionsQuery = useAllSubscriptions(deviceLimitExceptionList);
  const invitesQuery = useAllInvites(deviceLimitExceptionList);
  const nyCampaignQuery = useNYCampaignUsers(deviceLimitExceptionList);
  const collectionCountsQuery = useCollectionCounts();
  const freeTrialsQuery = useFreeTrials();
  const usersQuery = useAllUsers(deviceLimitExceptionList);
  const staffStudentsQuery = useStaffStudents();

  // Check if any query is loading
  const isLoading = [
    subscriptionsQuery.isLoading,
    invitesQuery.isLoading,
    nyCampaignQuery.isLoading,
    collectionCountsQuery.isLoading,
    freeTrialsQuery.isLoading,
    usersQuery.isLoading,
    staffStudentsQuery.isLoading,
  ].some(Boolean);

  // Check if any query has error
  const hasError = [
    subscriptionsQuery.isError,
    invitesQuery.isError,
    nyCampaignQuery.isError,
    collectionCountsQuery.isError,
    freeTrialsQuery.isError,
    usersQuery.isError,
    staffStudentsQuery.isError,
  ].some(Boolean);

  // Get user document for auth check
  useEffect(() => {
    const checkAuth = async () => {
      const firebaseUser = firebase.auth().currentUser;
      if (!firebaseUser) {
        setError("Please log in to view summary reports");
        return;
      }

      setUid(firebaseUser.uid);

      try {
        const user = await firebase
          .firestore()
          .collection("users")
          .doc(firebaseUser.uid)
          .get();

        if (!user.exists) {
          setError("There was an error retrieving your info");
          return;
        }

        const userRole = user.data()?.role;
        setRole(userRole);

        if (!["admin", "data-admin"].includes(userRole)) {
          setError("You are not authorized to access this page.");
        }
      } catch (err) {
        console.error("Error checking auth:", err);
        setError("There was an error retrieving your info");
      }
    };

    checkAuth();
  }, []);

  // Process subscriptions data and derived values
  const processedData = useMemo(() => {
    if (!subscriptionsQuery.data) {
      return {
        subscriptions: [],
        elavonSubs: [],
        stripeSubs: [],
        appleSubs: [],
        androidSubs: [],
        elavonSubs2: [],
        stripeSubs2: [],
        appleSubs2: [],
        androidSubs2: [],
        elavonCanceledSubs: [],
        stripeCanceledSubs: [],
        appleCanceledSubs: [],
        androidCanceledSubs: [],
        totalDataValues: [],
        totalActiveDataValues: [],
        totalCanceledDataValues: [],
        allCanceledUsers: [],
      };
    }

    const {
      allSubs,
      elavonSubs,
      stripeSubs,
      appleSubs,
      androidSubs,
      elavonSubs2,
      stripeSubs2,
      appleSubs2,
      androidSubs2,
      elavonCanceledSubs,
      stripeCanceledSubs,
      appleCanceledSubs,
      androidCanceledSubs,
    } = subscriptionsQuery.data;

    // Calculate allCanceledUsers
    const allCanceledUsers = allSubs.filter(
      (sub) =>
        (sub.autoRenewal === false || sub.endDate < String(Date.now())) &&
        sub.productId.includes("enterprise") === false
    );

    // Prepare subscription arrays for processing
    const totalDataValues =
      elavonSubs.length > 0 && appleSubs.length > 0 && androidSubs.length > 0
        ? [elavonSubs, appleSubs, androidSubs, stripeSubs]
        : [];

    const totalActiveDataValues =
      elavonSubs2.length > 0 && appleSubs2.length > 0 && androidSubs2.length > 0
        ? [elavonSubs2, appleSubs2, androidSubs2, stripeSubs2]
        : [];

    const totalCanceledDataValues =
      elavonCanceledSubs.length > 0 &&
      appleCanceledSubs.length > 0 &&
      androidCanceledSubs.length > 0
        ? [
            elavonCanceledSubs,
            appleCanceledSubs,
            androidCanceledSubs,
            stripeCanceledSubs,
          ]
        : [];

    return {
      subscriptions: allSubs,
      elavonSubs,
      stripeSubs,
      appleSubs,
      androidSubs,
      elavonSubs2,
      stripeSubs2,
      appleSubs2,
      androidSubs2,
      elavonCanceledSubs,
      stripeCanceledSubs,
      appleCanceledSubs,
      androidCanceledSubs,
      totalDataValues,
      totalActiveDataValues,
      totalCanceledDataValues,
      allCanceledUsers,
    };
  }, [subscriptionsQuery.data]);

  // Process users and calculate totalStudentsData
  const usersData = useMemo(() => {
    if (!usersQuery.data || !invitesQuery.data) {
      return {
        users: [],
        allCoaches: [],
        allStudents: [],
        totalStudentsData: [],
        instructorsNotSub: [],
        downgradeUsers: [],
      };
    }

    const { allUsers, allCoaches, allStudents } = usersQuery.data;
    const { allCoachesInvites, allStudentsInvites } = invitesQuery.data;

    // Calculate totalStudentsData
    let totalStudentsData = [];
    if (
      allStudents.length > 0 &&
      allCoaches.length > 0 &&
      allCoachesInvites.length > 0 &&
      allStudentsInvites.length > 0
    ) {
      const today = new Date();
      let allUsersResult = [
        {
          students: "Students",
          last24Hours: 0,
          last7Days: 0,
          lastMonth: 0,
          overall: 0,
        },
        {
          students: "Coaches",
          last24Hours: 0,
          last7Days: 0,
          lastMonth: 0,
          overall: 0,
        },
        {
          students: "Coach Invites Sent",
          last24Hours: 0,
          last7Days: 0,
          lastMonth: 0,
          overall: 0,
        },
        {
          students: "Student Invites Sent",
          last24Hours: 0,
          last7Days: 0,
          lastMonth: 0,
          overall: 0,
        },
        {
          students: "Total Users",
          last24Hours: 0,
          last7Days: 0,
          lastMonth: 0,
          overall: 0,
        },
        {
          students: "Total Invites Sent",
          last24Hours: 0,
          last7Days: 0,
          lastMonth: 0,
          overall: 0,
        },
      ];

      [allStudents, allCoaches, allCoachesInvites, allStudentsInvites].forEach(
        (value, index) => {
          value.forEach((user) => {
            let start = 0;
            if (user.userCreated) {
              start = moment(parseInt(user.userCreated));
            } else {
              start = moment(parseInt(user.createdAt));
            }
            let hours = moment.duration(start.diff(moment(today))).asHours();
            let days = moment.duration(start.diff(moment(today))).asDays();
            let months = moment.duration(start.diff(moment(today))).asMonths();

            if (-hours <= 24) {
              allUsersResult[index].last24Hours++;
              if (user.userCreated) {
                allUsersResult[4].last24Hours++;
              } else {
                allUsersResult[5].last24Hours++;
              }
            }
            if (-days <= 7) {
              allUsersResult[index].last7Days++;
              if (user.userCreated) {
                allUsersResult[4].last7Days++;
              } else {
                allUsersResult[5].last7Days++;
              }
            }
            if (-months <= 1) {
              allUsersResult[index].lastMonth++;
              if (user.userCreated) {
                allUsersResult[4].lastMonth++;
              } else {
                allUsersResult[5].lastMonth++;
              }
            }
            allUsersResult[index].overall++;
            if (user.userCreated) {
              allUsersResult[4].overall++;
            } else {
              allUsersResult[5].overall++;
            }
          });
        }
      );
      totalStudentsData = allUsersResult;
    }

    // Calculate instructorsNotSub
    const instructorsNotSub =
      allCoaches.length > 0 && processedData.subscriptions.length > 0
        ? allCoaches.filter(
            (ar) =>
              !processedData.subscriptions.find((rm) => rm.userId === ar.id)
          )
        : [];

    // Calculate downgradeUsers
    const downgradeUsers =
      allUsers.length > 0 && processedData.subscriptions.length > 0
        ? processedData.subscriptions.filter((ar) =>
            allUsers.find(
              (rm) =>
                rm.id === ar.userId && rm.subscriptionType !== ar.productId
            )
          )
        : [];

    return {
      users: allUsers,
      allCoaches,
      allStudents,
      totalStudentsData,
      instructorsNotSub,
      downgradeUsers,
    };
  }, [usersQuery.data, invitesQuery.data, processedData.subscriptions.length]);

  return {
    // Auth
    uid,
    role,
    error,
    // Data
    processedData,
    usersData,
    deviceLimitExceptionList,
    invites: invitesQuery.data?.allInvites || [],
    nyCampaignUsers: nyCampaignQuery.data || [],
    collCounts: collectionCountsQuery.data || [],
    trialSubs: freeTrialsQuery.data || [],
    staffStudents: staffStudentsQuery.data || [],
    // State
    isLoading,
    hasError,
  };
};
